import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeRedirects.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Plus } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositePagination from '../components/MicrositePagination/MicrositePagination'
import MicrositeRedirectTypeBadge from '../components/MicrositeRedirectTypeBadge/MicrositeRedirectTypeBadge'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'
import MicrositeInlineEditRow from '../components/MicrositeInlineEditRow/MicrositeInlineEditRow'
import TextInput from '../components/TextInput/TextInput'
import SelectInput from '../components/SelectInput/SelectInput'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'

const REDIRECT_TYPE_OPTIONS = [
    { value: 301, label: '301 — Permanent' },
    { value: 302, label: '302 — Temporary' }
]

const EMPTY_REDIRECT = { fromPath: '', toPath: '', redirectType: 301 }
const PAGE_LIMIT = 20

const MicrositeRedirects = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])
    const [cursorStack, setCursorStack] = useState([0])
    const [nextCursor, setNextCursor] = useState(null)

    const [adding, setAdding] = useState(false)
    const [newRedirect, setNewRedirect] = useState(EMPTY_REDIRECT)
    const [savingNew, setSavingNew] = useState(false)

    const currentCursor = cursorStack[cursorStack.length - 1]

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadRedirects = useCallback(async (cursor) => {
        setLoading(true)
        const res = await post('micrositeGetRedirects', { limit: PAGE_LIMIT, cursor: cursor || 0 })
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
            setNextCursor(res.response?.nextCursor ?? null)
        } else {
            showToast('Could not load redirects. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadRedirects(currentCursor)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, currentCursor])

    const handleSaveNew = async () => {
        if (!newRedirect.fromPath.trim() || !newRedirect.toPath.trim()) {
            showToast('Both from path and to path are required', 'error')
            return
        }
        setSavingNew(true)
        const res = await post('micrositeCreateRedirect', { ...newRedirect })
        setSavingNew(false)

        if (res?.status === 'success') {
            showToast('Redirect created successfully')
            setAdding(false)
            setNewRedirect(EMPTY_REDIRECT)
            loadRedirects(currentCursor)
        } else {
            showToast('Could not create redirect', 'error')
        }
    }

    const handleToggleActive = async (row, isActive) => {
        setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive } : r)))
        const res = await post('micrositeUpdateRedirect', {
            ID: row.ID,
            fromPath: row.fromPath,
            toPath: row.toPath,
            redirectType: row.redirectType,
            isActive
        })
        if (res?.status !== 'success') {
            showToast('Could not update redirect status', 'error')
            setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive: !isActive } : r)))
        }
    }

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeleteRedirect', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('Redirect deleted')
        } else {
            showToast('Could not delete redirect', 'error')
        }
    }

    const columns = [
        {
            key: 'fromPath',
            label: 'From',
            render: (row) => <span className={styles.path}>{row.fromPath}</span>
        },
        {
            key: 'toPath',
            label: 'To',
            render: (row) => <span className={styles.path}>{row.toPath}</span>
        },
        {
            key: 'redirectType',
            label: 'Type',
            render: (row) => <MicrositeRedirectTypeBadge value={row.redirectType} />
        },
        {
            key: 'hitCount',
            label: 'Hits',
            align: 'right',
            render: (row) => <span className={styles.hits}>{row.hitCount ?? 0}</span>
        },
        {
            key: 'isActive',
            label: 'Active',
            render: (row) => (
                <ToggleSwitch size="sm" checked={!!row.isActive} onChange={(v) => handleToggleActive(row, v)} />
            )
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Redirects"
                backTo="/microsite/seo"
                backLabel="SEO settings"
                subtitle="Send visitors from an old URL to a new one."
                primaryAction={{ label: 'Add redirect', icon: <Plus size={14} />, onClick: () => setAdding(true) }}
            />

            {adding && (
                <MicrositeInlineEditRow onSave={handleSaveNew} onCancel={() => { setAdding(false); setNewRedirect(EMPTY_REDIRECT) }} saving={savingNew}>
                    <TextInput
                        label="From path"
                        value={newRedirect.fromPath}
                        onChange={(v) => setNewRedirect((p) => ({ ...p, fromPath: v }))}
                        placeholder="/old-page"
                    />
                    <TextInput
                        label="To path"
                        value={newRedirect.toPath}
                        onChange={(v) => setNewRedirect((p) => ({ ...p, toPath: v }))}
                        placeholder="/new-page"
                    />
                    <SelectInput
                        label="Type"
                        value={newRedirect.redirectType}
                        onChange={(v) => setNewRedirect((p) => ({ ...p, redirectType: Number(v) }))}
                        options={REDIRECT_TYPE_OPTIONS}
                    />
                </MicrositeInlineEditRow>
            )}

            <MicrositeTable
                columns={columns}
                rows={rows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No redirects yet"
                emptyDescription="Add one to redirect visitors from an old URL to a new one"
            />

            <MicrositePagination
                hasPrev={cursorStack.length > 1}
                hasNext={!!nextCursor}
                onPrev={() => setCursorStack((s) => s.slice(0, -1))}
                onNext={() => setCursorStack((s) => [...s, nextCursor])}
                loading={loading}
                currentCount={rows.length}
            />
        </div>
    )
}

export default MicrositeRedirects